import { BGM_VOLUME_BROWSER, sfxVolume } from "../audio/levels.js";
import { getAudioPrefs } from "../audio/prefs.js";
import { HTTP_BASE, WS_URL } from "../config.js";
import type {
  GameCanvas,
  HttpJson,
  KeyEvent,
  Platform,
  PointerEvent,
  Size,
  SocketHandle,
  SocketHandlers,
} from "./types.js";

const BGM_VOLUME = BGM_VOLUME_BROWSER;
const SFX_VOLUME = sfxVolume(BGM_VOLUME);

let browserBgm: HTMLAudioElement | null = null;
let browserBgmBound = false;
let browserBgmGesture = false;

function tryPlayBrowserBgm(): void {
  const audio = browserBgm;
  if (!audio) return;
  if (getAudioPrefs().bgmMuted) {
    audio.pause();
    return;
  }
  if (!audio.paused) return;
  audio.muted = false;
  audio.volume = BGM_VOLUME;
  const pending = audio.play();
  if (pending && typeof pending.catch === "function") {
    pending.catch((err: unknown) => {
      const name = err && typeof err === "object" && "name" in err ? String((err as { name: unknown }).name) : "";
      if (name && name !== "NotAllowedError" && name !== "AbortError") {
        console.log("[gaoya] bgm play failed", err);
      }
    });
  }
}

function noteBgmGesture(): void {
  browserBgmGesture = true;
  tryPlayBrowserBgm();
}

function startBrowserBgm(src: string): void {
  const url = src.startsWith("/") || src.startsWith("http") ? src : `/${src}`;
  if (!browserBgm) {
    const audio = new Audio();
    audio.src = url;
    audio.loop = true;
    audio.preload = "auto";
    audio.autoplay = false;
    audio.controls = false;
    audio.muted = false;
    audio.volume = BGM_VOLUME;
    audio.setAttribute("playsinline", "true");
    audio.setAttribute("aria-hidden", "true");
    audio.style.display = "none";
    audio.addEventListener("canplay", () => {
      if (browserBgmGesture) tryPlayBrowserBgm();
    });
    audio.addEventListener("error", () => console.log("[gaoya] bgm load error", audio.error?.message ?? audio.src));
    document.body.appendChild(audio);
    browserBgm = audio;
    audio.load();
  }
  if (!browserBgmBound) {
    browserBgmBound = true;
    const kick = () => noteBgmGesture();
    for (const type of ["pointerdown", "pointerup", "touchstart", "touchend", "click", "keydown"] as const) {
      window.addEventListener(type, kick, { capture: true });
    }
  }
  tryPlayBrowserBgm();
}

function playBrowserSfx(src: string): void {
  if (getAudioPrefs().sfxMuted) return;
  const url = src.startsWith("/") || src.startsWith("http") ? src : `/${src}`;
  try {
    const audio = new Audio(url);
    audio.preload = "auto";
    audio.volume = SFX_VOLUME;
    const pending = audio.play();
    if (pending && typeof pending.catch === "function") {
      pending.catch((err: unknown) => {
        const name = err && typeof err === "object" && "name" in err ? String((err as { name: unknown }).name) : "";
        if (name && name !== "NotAllowedError" && name !== "AbortError") {
          console.log("[gaoya] sfx play failed", err);
        }
      });
    }
  } catch {
    /* ignore */
  }
}

function qsWs(): string | null {
  try {
    const q = new URLSearchParams(window.location.search).get("ws");
    return q && q.length > 0 ? q : null;
  } catch {
    return null;
  }
}

function isLocalPreview(): boolean {
  try {
    const host = window.location.hostname;
    const port = window.location.port;
    return host === "localhost" || host === "127.0.0.1" || port === "4173";
  } catch {
    return false;
  }
}

function defaultWs(): string {
  if (isLocalPreview()) return WS_URL;
  const proto = window.location.protocol === "https:" ? "wss" : "ws";
  return `${proto}://${window.location.host}/ws`;
}

function httpBase(): string {
  if (isLocalPreview()) return HTTP_BASE;
  return window.location.origin;
}

export function createBrowserPlatform(): Platform {
  const canvas = document.createElement("canvas");
  canvas.setAttribute("aria-label", "接词大乱斗");
  document.body.appendChild(canvas);
  const ctxProbe = canvas.getContext("2d");
  if (!ctxProbe) throw new Error("2d canvas unavailable");

  let dpr = Math.max(1, window.devicePixelRatio || 1);
  const logical: Size = { w: window.innerWidth, h: window.innerHeight };

  const applySize = (): void => {
    dpr = Math.max(1, window.devicePixelRatio || 1);
    logical.w = Math.max(1, window.innerWidth);
    logical.h = Math.max(1, window.innerHeight);
    canvas.width = Math.floor(logical.w * dpr);
    canvas.height = Math.floor(logical.h * dpr);
    canvas.style.width = `${logical.w}px`;
    canvas.style.height = `${logical.h}px`;
  };
  applySize();

  const toLocal = (clientX: number, clientY: number): { x: number; y: number } => {
    const rect = canvas.getBoundingClientRect();
    const x = ((clientX - rect.left) / Math.max(1, rect.width)) * logical.w;
    const y = ((clientY - rect.top) / Math.max(1, rect.height)) * logical.h;
    return { x, y };
  };

  const platform: Platform = {
    kind: "browser",
    canvas: canvas as unknown as GameCanvas,
    logical,
    get dpr() {
      return dpr;
    },
    now: () => Date.now(),
    raf: (cb) => window.requestAnimationFrame(cb),
    onResize(cb) {
      window.addEventListener("resize", () => {
        applySize();
        cb({ w: logical.w, h: logical.h });
      });
    },
    onPointer(cb) {
      const fire = (kind: PointerEvent["kind"], ev: PointerEventInit & { clientX: number; clientY: number; pointerId?: number }) => {
        const p = toLocal(ev.clientX, ev.clientY);
        cb({ kind, x: p.x, y: p.y, id: ev.pointerId ?? 0 });
      };
      canvas.addEventListener(
        "pointerdown",
        (e) => {
          noteBgmGesture();
          e.preventDefault();
          try {
            canvas.setPointerCapture(e.pointerId);
          } catch {
            /* some environments reject capture on synthetic events */
          }
          fire("down", e);
        },
        { passive: false },
      );
      canvas.addEventListener(
        "pointermove",
        (e) => {
          e.preventDefault();
          fire("move", e);
        },
        { passive: false },
      );
      canvas.addEventListener(
        "pointerup",
        (e) => {
          e.preventDefault();
          fire("up", e);
        },
        { passive: false },
      );
      canvas.addEventListener("pointercancel", (e) => fire("cancel", e));
      canvas.addEventListener(
        "touchstart",
        (e) => {
          noteBgmGesture();
          e.preventDefault();
        },
        { passive: false },
      );
    },
    onKey(cb) {
      window.addEventListener("keydown", (e) => {
        const ev: KeyEvent = { key: e.key, repeat: e.repeat };
        cb(ev);
      });
    },
    vibrateShort() {
      if (getAudioPrefs().vibrateMuted) return;
      try {
        if (navigator.vibrate) navigator.vibrate(12);
      } catch {
        /* ignore */
      }
    },
    connectSocket(url, handlers: SocketHandlers): SocketHandle {
      const ws = new WebSocket(url);
      ws.addEventListener("open", () => handlers.onOpen());
      ws.addEventListener("message", (e) => {
        if (typeof e.data === "string") handlers.onMessage(e.data);
      });
      ws.addEventListener("close", () => handlers.onClose());
      ws.addEventListener("error", () => handlers.onError("ws error"));
      return {
        send(data) {
          if (ws.readyState === WebSocket.OPEN) ws.send(data);
        },
        close() {
          try {
            ws.close();
          } catch {
            /* ignore */
          }
        },
      };
    },
    loadImage(src) {
      return new Promise((resolve) => {
        const img = new Image();
        const url = src.startsWith("/") || src.startsWith("http") ? src : `/${src}`;
        if (/^https?:\/\//i.test(url)) img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = url;
      });
    },
    getStorage(key) {
      try {
        return window.localStorage.getItem(key);
      } catch {
        return null;
      }
    },
    setStorage(key, value) {
      try {
        window.localStorage.setItem(key, value);
      } catch {
        /* ignore */
      }
    },
    async httpJson(method, url, body): Promise<HttpJson> {
      try {
        const init: RequestInit = { method };
        if (body !== undefined) {
          init.headers = { "Content-Type": "application/json" };
          init.body = JSON.stringify(body);
        }
        const res = await fetch(url, init);
        const text = await res.text();
        let parsed: unknown = text;
        try {
          parsed = text ? JSON.parse(text) : null;
        } catch {
          parsed = text;
        }
        return { ok: res.ok, status: res.status, body: parsed };
      } catch {
        return { ok: false, status: 0, body: null };
      }
    },
    resolveWsUrl() {
      const q = qsWs();
      if (q) return { primary: q, fallback: null };
      return { primary: defaultWs(), fallback: null };
    },
    resolveHttpBase: httpBase,
    getLoginCode: async () => null,
    fetchAuthorizedUserInfo: async () => null,
    syncUserInfoButton() {
      /* 浏览器无微信授权按钮 */
    },
    hideUserInfoButton() {
      /* no-op */
    },
    requirePrivacyAuthorize: async () => true,
    openPrivacyContract() {
      /* 浏览器无微信隐私合同页 */
    },
    resolvePrivacyAuthorization() {
      /* no-op */
    },
    onPrivacyPrompt() {
      /* no-op */
    },
    publishWins() {
      /* 浏览器无微信托管数据 */
    },
    showFriendRank() {
      /* no-op */
    },
    hideFriendRank() {
      /* no-op */
    },
    getFriendRankCanvas: () => null,
    playLoopingBgm(src) {
      startBrowserBgm(src);
    },
    playSfx(src) {
      playBrowserSfx(src);
    },
    applyAudioPrefs() {
      tryPlayBrowserBgm();
    },
    safeInset() {
      return { top: 8, bottom: 0, capsuleLeft: logical.w, capsuleTop: 8, capsuleHeight: 32 };
    },
    async copyText(text) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch {
        return false;
      }
    },
    shareApp(payload) {
      void navigator.clipboard?.writeText(payload.title).catch(() => undefined);
    },
    setShareMenu() {
      /* 浏览器无系统分享菜单 */
    },
    getLaunchQuery() {
      try {
        const out: Record<string, string> = {};
        const params = new URLSearchParams(window.location.search);
        params.forEach((v, k) => {
          if (v) out[k] = v;
        });
        return out;
      } catch {
        return {};
      }
    },
    getLaunchScene() {
      return null;
    },
    onAppShow(cb) {
      document.addEventListener("visibilitychange", () => {
        if (!document.hidden) cb({});
      });
    },
    onAppHide(cb) {
      document.addEventListener("visibilitychange", () => {
        if (document.hidden) cb();
      });
    },
    createOffscreenCanvas(width, height) {
      const c = document.createElement("canvas");
      c.width = width;
      c.height = height;
      const ctx = c.getContext("2d");
      if (!ctx) return null;
      return { canvas: c as unknown as GameCanvas, ctx, width, height };
    },
    async exportCanvasPng(handle, filename) {
      try {
        const url = handle.canvas.toDataURL ? handle.canvas.toDataURL("image/png") : (handle.canvas as unknown as HTMLCanvasElement).toDataURL("image/png");
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        return url;
      } catch {
        return null;
      }
    },
    log: (...args) => console.log("[gaoya]", ...args),
  };

  return platform;
}
